import * as yup from "yup";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {
    AccountBalanceOutlined,
    ArrowForward,
    CancelOutlined,
    CheckCircleOutlined,
    ReceiptLongOutlined,
    SavingsOutlined,
} from "@mui/icons-material";

import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import TextInput from "../../../../components/hookForms/textField";
import messages from "../../../../utils/messages";
import serviceFaturaInstalacao from "../../../../services/serviceFaturaInstalacao";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Loader } from "./styles";

export interface IDialogProps {
    open: boolean;
    onClose?: any;
    onCancel?: any;
    idProp?: any;
}

const schema = yup.object().shape({
    motivo: yup.string().required(messages.requiredField),
});

const SectionTitle = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
    <Box display="flex" alignItems="center" gap={1} mb={1}>
        {icon}
        <Typography variant="subtitle1" fontWeight="bold">
            {label}
        </Typography>
    </Box>
);

const FlagChip = ({ value }: { value: string }) => (
    value === "Nao" || value === "Não"
        ? <Chip icon={<CheckCircleOutlined />} label="Não" size="small" color="success" variant="outlined" />
        : <Chip icon={<CancelOutlined />} label={value} size="small" color="error" variant="outlined" />
);

export default function DialogReverterReaberturaIndevida({ open, onClose, onCancel, idProp }: IDialogProps) {
    const [isDisable, setDisable] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [preview, setPreview] = useState<any>(null);
    const [previewErrors, setPreviewErrors] = useState<string[]>([]);
    const [editarSaldos, setEditarSaldos] = useState(false);
    const [saldosEditados, setSaldosEditados] = useState<Record<number, string>>({});

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { motivo: "Fatura reaberta manualmente" },
    });

    useEffect(() => {
        if (!open) return;

        setLoadingPreview(true);
        setPreview(null);
        setPreviewErrors([]);
        setEditarSaldos(false);
        setSaldosEditados({});

        serviceFaturaInstalacao
            .previewReverterReaberturaIndevida(idProp.id)
            .then((result) => {
                if (result && result.status === 200) {
                    if (result.data?.errors?.length > 0) {
                        setPreviewErrors(result.data.errors);
                    } else {
                        setPreview(result.data);
                    }
                }
            })
            .catch(() => {
                setPreviewErrors(["Erro ao carregar o preview da reversao."]);
            })
            .finally(() => {
                setLoadingPreview(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onSubmit = async (values: any) => {
        setDisable(true);

        const saldosPayload = preview?.saldosParaReverter?.map((s: any, i: number) => ({
            tipoSaldo: s.tipoSaldo,
            quantidadeKWAposReversao:
                editarSaldos && saldosEditados[i] !== undefined
                    ? parseFloat(saldosEditados[i].replace(",", "."))
                    : s.quantidadeKWAposReversao,
        }));

        serviceFaturaInstalacao
            .reverterReaberturaIndevida(idProp.id, values.motivo, saldosPayload)
            .then((result) => {
                if (result && result.status === 200) {
                    toast.success("Reabertura indevida revertida com sucesso!");
                    reset();
                    onClose(true);
                }
            })
            .catch(() => {
                toast.error("Ocorreu um erro ao reverter a reabertura indevida.");
            })
            .finally(() => {
                setDisable(false);
            });
    };

    const onCancelAction = () => {
        reset();
        setPreview(null);
        setPreviewErrors([]);
        setEditarSaldos(false);
        setSaldosEditados({});
        onCancel(true);
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            onClose={(_, reason) => {
                if (reason !== "backdropClick") onCancel();
            }}
            open={open}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                    Reverter Reabertura Indevida
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onCancel}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent dividers>
                    {isDisable && (
                        <Loader>
                            <img alt="Loading" src="/Loading.gif" width="40px" height="40px" />
                        </Loader>
                    )}

                    <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: "#f9f9f9" }}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">Fatura Instalacao ID</Typography>
                                <Typography variant="body2" fontWeight="bold">{idProp?.id ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">Num. Instalacao</Typography>
                                <Typography variant="body2" fontWeight="bold">{idProp?.numeroInstalacao ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">Ciclo</Typography>
                                <Typography variant="body2" fontWeight="bold">{idProp?.cicloFaturamento ?? "-"}</Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {loadingPreview ? (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5}>
                            <CircularProgress size={40} sx={{ color: "#019BBE", mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                Carregando preview da reversao...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {previewErrors.length > 0 && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                        {previewErrors.map((erro, i) => <li key={i}>{erro}</li>)}
                                    </ul>
                                </Alert>
                            )}

                            {preview && (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Alert severity="warning" sx={{ mb: 1, backgroundColor: "#fff3cd", border: "1px solid #e6a817", color: "#5c3d00", "& .MuiAlert-icon": { color: "#e6a817" } }}>
                                        As alterações abaixo serão aplicadas ao confirmar. Esta ação não pode ser desfeita.
                                    </Alert>

                                    {preview.faturaClienteParaReativar && (
                                        <Box>
                                            <SectionTitle icon={<ReceiptLongOutlined color="primary" fontSize="small" />} label="Fatura de Cliente a reativar" />
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead sx={{ backgroundColor: "#f0f4f8" }}>
                                                        <TableRow>
                                                            <TableCell><strong>Fatura Cliente ID</strong></TableCell>
                                                            <TableCell><strong>Num. Nota Sankhya</strong></TableCell>
                                                            <TableCell align="center"><strong>Flag Cancelada Atual</strong></TableCell>
                                                            <TableCell align="center"></TableCell>
                                                            <TableCell align="center"><strong>Apos Reversao</strong></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell>{preview.faturaClienteParaReativar.faturaClienteID ?? "-"}</TableCell>
                                                            <TableCell>{preview.faturaClienteParaReativar.codigoExternoCliente ?? "-"}</TableCell>
                                                            <TableCell align="center">
                                                                <FlagChip value={preview.faturaClienteParaReativar.flagCanceladaAtual ?? "-"} />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <ArrowForward fontSize="small" color="action" />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <FlagChip value={preview.faturaClienteParaReativar.flagCanceladaAposReversao ?? "-"} />
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {preview.faturasMesmoCicloParaAguardando?.length > 0 && (
                                        <Box>
                                            <SectionTitle icon={<AccountBalanceOutlined color="warning" fontSize="small" />} label="Faturas do mesmo ciclo (Aprovada para Publicada)" />
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead sx={{ backgroundColor: "#f0f4f8" }}>
                                                        <TableRow>
                                                            <TableCell><strong>ID</strong></TableCell>
                                                            <TableCell align="center"><strong>Status Atual</strong></TableCell>
                                                            <TableCell align="center"></TableCell>
                                                            <TableCell align="center"><strong>Apos Reversao</strong></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {preview.faturasMesmoCicloParaAguardando.map((f: any, i: number) => (
                                                            <TableRow key={i}>
                                                                <TableCell>{f.faturaInstalacaoID ?? "-"}</TableCell>
                                                                <TableCell align="center">
                                                                    <Chip label={f.statusAtual ?? "-"} size="small" color="primary" variant="outlined" />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <ArrowForward fontSize="small" color="action" />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Chip label="Publicada" size="small" color="warning" variant="outlined" />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {preview.saldosParaReverter?.length > 0 && (
                                        <Box>
                                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                <SectionTitle icon={<SavingsOutlined color="action" fontSize="small" />} label="Saldos a reverter" />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={editarSaldos}
                                                            onChange={(e) => {
                                                                setEditarSaldos(e.target.checked);
                                                                if (!e.target.checked) setSaldosEditados({});
                                                            }}
                                                        />
                                                    }
                                                    label="✏️ Editar saldo antes de enviar"
                                                    sx={{ mb: 0 }}
                                                />
                                            </Box>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead sx={{ backgroundColor: "#f0f4f8" }}>
                                                        <TableRow>
                                                            <TableCell><strong>Tipo de Saldo</strong></TableCell>
                                                            <TableCell align="right"><strong>Qtd. KW Atual</strong></TableCell>
                                                            <TableCell align="center"></TableCell>
                                                            <TableCell align="right"><strong>Qtd. KW Apos Reversao</strong></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {preview.saldosParaReverter.map((s: any, i: number) => (
                                                            <TableRow key={i}>
                                                                <TableCell>{s.tipoSaldo ?? "-"}</TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2" color={s.quantidadeKWAtual !== s.quantidadeKWAposReversao ? "error" : "text.primary"}>
                                                                        {s.quantidadeKWAtual?.toLocaleString("pt-BR") ?? "-"}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <ArrowForward fontSize="small" color="action" />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {editarSaldos ? (
                                                                        <TextField
                                                                            size="small"
                                                                            variant="outlined"
                                                                            inputProps={{ style: { textAlign: "right", width: 120 } }}
                                                                            value={
                                                                                saldosEditados[i] !== undefined
                                                                                    ? saldosEditados[i]
                                                                                    : (s.quantidadeKWAposReversao ?? "")
                                                                            }
                                                                            onChange={(e) =>
                                                                                setSaldosEditados((prev) => ({ ...prev, [i]: e.target.value }))
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <Typography
                                                                            variant="body2"
                                                                            color={s.quantidadeKWAtual !== s.quantidadeKWAposReversao ? "success.main" : "text.primary"}
                                                                            fontWeight={s.quantidadeKWAtual !== s.quantidadeKWAposReversao ? "bold" : "normal"}
                                                                        >
                                                                            {s.quantidadeKWAposReversao?.toLocaleString("pt-BR") ?? "-"}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    <Divider />

                                    <Grid container spacing={1}>
                                        <TextInput
                                            name="motivo"
                                            label="Descreva o Motivo da Reversao"
                                            control={control}
                                            error={errors}
                                            multiline
                                            maxLength={150}
                                        />
                                    </Grid>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                    <Button
                        onClick={onCancelAction}
                        color="warning"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isDisable || loadingPreview || previewErrors.length > 0 || !preview}
                        style={{ width: "159px", height: "44px" }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
